"use client";

import { createContext, useContext } from "react";

/**
 * The signed-in user's avatar, presigned. `session.user.avatarPath` (from the
 * NextAuth JWT) is a raw storage path — `toImageUrl` is server-only and can
 * never run in a client component, so a server component must presign it and
 * hand the result down. Root layout does that once per render and provides it
 * here so any client descendant (app shell, profile form) can read it without
 * threading a prop through the opaque `children` tree.
 *
 * Deliberately NOT stored on the session/JWT: a presigned URL expires in ~2h,
 * far shorter than the session's cookie lifetime, so baking it into the token
 * would eventually serve an expired link. Presigning at render means it's at
 * most as stale as the last full page load/navigation that re-ran the root
 * layout — much tighter than the JWT's refresh cadence.
 */
const AvatarUrlContext = createContext<string | null>(null);

export function AvatarUrlProvider({ value, children }: { value: string | null; children: React.ReactNode }) {
  return <AvatarUrlContext.Provider value={value}>{children}</AvatarUrlContext.Provider>;
}

export function useAvatarUrl(): string | null {
  return useContext(AvatarUrlContext);
}
